import classNames from 'classnames';
import get from 'lodash/get';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IconTable, Interpolate, Link } from '@wg/wows-react-uikit';

import { root } from '~/preloaded';
import { hasCrossRealm } from '~/settings';
import getSeasonIconUrl from '~/helpers/clanIcons';
import { percent } from '~/helpers/formatting';
import { getLeagueNameByNumber } from '~/helpers/ladder';
import { t } from '~/helpers/localization';
import { ROUTES_MAP } from '~/router/routes';

import styles from './TopWinners.scss';

import type { OwnClan, Winner } from '~/Reducers/ReducerHallFame';
import type { Nullable, Nullish } from '~/types/generics';

const chineseReal = 'cn';

interface TopWinners_Props {
  className?: string;
  season: Array<Winner>;
  ratings?: Array<OwnClan>;
  seasonName: {
    [season: string]: string;
  };
  ownClansTag?: Nullable<string>;
  ownClansName?: Nullable<string>;
  onSeasonChange: (selectedTab: number) => void;
  realmsMap: {
    [realm: string]: {
      [name: string]: string;
    };
  };
  isHide?: boolean;
  isShow?: boolean;
}

const TopWinners: React.FC<TopWinners_Props> = ({
  className,
  season,
  seasonName,
  ownClansTag,
  ownClansName,
  ratings,
  realmsMap,
  isShow,
  isHide,
  onSeasonChange,
}) => {
  const navigate = useNavigate();

  let rating: undefined | OwnClan;
  let leagueName: Nullish<string>;
  if (ratings) {
    rating = ratings[0];
    leagueName = getLeagueNameByNumber(rating.league, season[0].season_id);
  }

  const onClick = (numberSeason: number) => {
    navigate(`${root}${ROUTES_MAP.CLAN_BATTLES_RATING}`);
    onSeasonChange(numberSeason);
  };

  const url = season[0] && season[0].season_id ? getSeasonIconUrl(season[0].season_id) : '';

  return (
    <div
      className={classNames(styles.wrapper, className, {
        [styles.isHide]: isHide,
        [styles.isShow]: isShow,
      })}
    >
      <header className={styles.header}>
        <h4 className={styles.title}>
          <Interpolate
            useDangerouslySetInnerHTML={true}
            str={t('Сезон  %(number)s: %(name)s')}
            number={season[0].season_id}
            name={seasonName[season[0].season_id]}
          />
        </h4>
        <img className={styles.seasonIcon} src={url} />
      </header>
      <ul className={styles.winners}>
        {season.map((winner: Winner) => {
          if (winner.realm !== chineseReal) {
            return (
              <li className={styles.item} key={winner.clan_id}>
                <div>
                  {hasCrossRealm && (
                    <span className={styles.realm}>{get(realmsMap, [`${winner.realm}`, 'shortName'])}</span>
                  )}
                  <span className={styles.tag}>[{winner.tag}]</span>
                </div>
                <span className={styles.name}>{winner.name}</span>
                <p className={styles.position}>
                  <span className={styles.rating}>
                    <IconTable glyph={'rating'} />
                    {'CR: '}
                    {winner.division_rating}
                  </span>
                  <span>
                    {`${t('Battles')}: `}
                    {winner.battles_count}
                  </span>
                </p>
                <span className={styles.winsCount}>
                  {'W/B: '}
                  {percent((winner.wins_count / winner.battles_count) * 100)}
                </span>
              </li>
            );
          }
        })}
      </ul>
      {ownClansTag && (
        <footer className={styles.footer}>
          <div className={styles.ownClan}>
            <div className={styles.clanPosition}>{t('Позиция моего клана:')}</div>
            {rating && (
              <div>
                <span className={styles.ownClansTag}>
                  {'['}
                  {ownClansTag}
                  {'] '}
                </span>
                <span>{ownClansName}</span>
              </div>
            )}
            {rating ? (
              <>
                <div className={styles.league}>
                  <IconTable glyph={'rating'} />
                  <span className={styles.info}>
                    {t('Лига: ')}
                    {leagueName}
                    {', '}
                  </span>
                  {rating.league !== 0 && (
                    <span className={styles.info}>
                      {t(' Группа: ')}
                      {rating.division}
                      {', '}
                    </span>
                  )}
                  <span>
                    {' CR: '}
                    {rating.division_rating}
                  </span>
                </div>
              </>
            ) : (
              <div className={styles.outRate}>{t('Ваш клан отсутствует в рейтинге')}</div>
            )}
          </div>
          {rating && (
            <Link
              className={styles.linkToClan}
              isActionLink={false}
              arrow=""
              onClick={() => onClick(season[0].season_id)}
            >
              {t('Перейти к позиции моего клана')}
            </Link>
          )}
        </footer>
      )}
    </div>
  );
};

export default TopWinners;
